/**
 * ChatBubble — floating ET-Assistant.
 *
 * Visible bottom-right on every authenticated user page (mounted inside
 * UserLayout). When the user opens it, a panel slides up with:
 *   - Header (title + "Neuer Chat" reset + close button)
 *   - Scrollable message list (user vs. assistant bubbles, Markdown-ish styled)
 *   - Input row with Send button
 *
 * State:
 *   - messages: chat history (persisted in localStorage per user, keyed
 *     by user id so different accounts on the same machine stay separate).
 *   - sessionId: stable per browser tab — sent to backend purely for log tracing.
 *   - currentKv: pulled from ChatContext so the backend can deep-context.
 *
 * Privacy:
 *   - The backend never persists chat messages. The browser is the single source
 *     of truth. "Neuer Chat" wipes localStorage for this account.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useChatContext } from "../context/ChatContext";
import {
  ChatCircleDots,
  X,
  PaperPlaneRight,
  ArrowCounterClockwise,
  Sparkle,
  Shield,
} from "@phosphor-icons/react";

const STORAGE_PREFIX = "et:chat:v1:";
const SESSION_PREFIX = "et:chat-session:v1:";
const MAX_HISTORY_TURNS = 24; // rolling window — must stay <= backend MAX_MESSAGES_PER_REQUEST

function makeSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `sess-${crypto.randomUUID().slice(0, 12)}`;
  }
  return `sess-${Math.random().toString(36).slice(2, 14)}`;
}

/**
 * Parse a single SSE frame ("event: …\ndata: …") into {event, data}.
 * Returns null for empty / malformed frames.
 */
function parseSseFrame(frame) {
  const trimmed = frame.trim();
  if (!trimmed) return null;
  let event = "message";
  let dataRaw = "";
  for (const line of trimmed.split("\n")) {
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataRaw += (dataRaw ? "\n" : "") + line.slice(5).trim();
    }
  }
  let data = null;
  if (dataRaw) {
    try {
      data = JSON.parse(dataRaw);
    } catch {
      data = dataRaw;
    }
  }
  return { event, data };
}

const WELCOME = {
  role: "assistant",
  content:
    "Willkommen! Ich bin der ET-Assistent. Stellen Sie mir Fragen zur Bedienung des Tools, zur KV-Einstufung allgemein oder zu konkreten Regelungen des aktuell ausgewählten Kollektivvertrags.",
};

export default function ChatBubble() {
  const { user } = useAuth();
  const { currentKv } = useChatContext();
  const userKey = user?.id || "anon";
  const storageKey = `${STORAGE_PREFIX}${userKey}`;
  const sessionKey = `${SESSION_PREFIX}${userKey}`;

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {
      // corrupted state — start fresh
    }
    return [WELCOME];
  });
  const [sessionId, setSessionId] = useState(() => {
    try {
      const existing = localStorage.getItem(sessionKey);
      if (existing && existing.length >= 4) return existing;
    } catch {
      // ignore
    }
    const fresh = makeSessionId();
    try {
      localStorage.setItem(sessionKey, fresh);
    } catch {
      // ignore
    }
    return fresh;
  });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Persist messages whenever they change.
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(messages));
    } catch {
      // localStorage quota / disabled — ignore (chat keeps working in-memory)
    }
  }, [messages, storageKey]);

  // Auto-scroll to bottom on new messages / open.
  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open, sending]);

  // Auto-focus input when opening.
  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError("");
    setInput("");
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setSending(true);

    // Rolling window: trim history sent to backend (keep the welcome message
    // off the wire — it's purely client UX scaffolding).
    const apiHistory = next
      .filter((m) => !(m.role === "assistant" && m === WELCOME))
      .slice(-MAX_HISTORY_TURNS);

    // Append an empty assistant turn — we mutate it incrementally as SSE
    // chunks arrive. Index = next.length (assistant slot one after the user msg).
    const assistantIdx = next.length;
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    // SSE via fetch+ReadableStream — EventSource would force GET, we need POST.
    let aborted = false;
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${base}/api/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          kv_id: currentKv?.id || undefined,
          messages: apiHistory,
        }),
      });
      if (!resp.ok || !resp.body) {
        let detail = "Der ET-Assistent ist gerade nicht erreichbar.";
        try {
          const j = await resp.json();
          if (j?.detail) detail = j.detail;
        } catch {
          // ignore
        }
        throw new Error(detail);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let assembled = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE frame separator is a blank line. Split on \n\n and keep tail.
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          const ev = parseSseFrame(frame);
          if (!ev) continue;
          if (ev.event === "chunk" && typeof ev.data?.text === "string") {
            assembled += ev.data.text;
            const snapshot = assembled;
            setMessages((prev) => {
              const copy = prev.slice();
              if (copy[assistantIdx]) {
                copy[assistantIdx] = { role: "assistant", content: snapshot };
              }
              return copy;
            });
          } else if (ev.event === "error") {
            throw new Error(ev.data?.detail || "Unbekannter Fehler vom Chat-Backend.");
          } else if (ev.event === "done") {
            aborted = false;
          }
        }
      }
    } catch (err) {
      aborted = true;
      setError(err.message || "Der ET-Assistent ist gerade nicht erreichbar.");
      // Drop the empty assistant turn so the bubble stays clean.
      setMessages((prev) => {
        if (prev.length > assistantIdx && prev[assistantIdx].content === "") {
          const copy = prev.slice();
          copy.splice(assistantIdx, 1);
          return copy;
        }
        return prev;
      });
    } finally {
      setSending(false);
      if (aborted) {
        // already cleaned up above
      }
    }
  };

  const resetChat = () => {
    if (sending) return;
    setMessages([WELCOME]);
    const fresh = makeSessionId();
    setSessionId(fresh);
    try {
      localStorage.removeItem(storageKey);
      localStorage.setItem(sessionKey, fresh);
    } catch {
      // ignore
    }
    setError("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const visibleMessages = useMemo(() => messages, [messages]);

  return (
    <>
      {/* Floating button — bottom right */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chat-bubble-toggle"
          aria-label="ET-Assistent öffnen"
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 bg-brand-400 text-white font-semibold text-sm px-4 py-3 rounded-full shadow-[0_8px_24px_rgba(212,134,68,0.35)] hover:bg-brand-500 transition-crisp"
        >
          <ChatCircleDots size={18} weight="bold" />
          ET-Assistent
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-40 w-[min(420px,calc(100vw-2rem))] h-[min(620px,calc(100vh-4rem))] bg-white border border-ink-200 rounded-sm shadow-2xl flex flex-col"
          data-testid="chat-panel"
          role="dialog"
          aria-label="ET-Assistent"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-ink-200 bg-ink-900 text-white flex items-center gap-3">
            <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-brand-400/20">
              <Sparkle size={16} weight="bold" className="text-brand-300" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-cabinet text-base tracking-tight font-medium leading-tight">
                ET-Assistent
              </div>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-white/60 truncate">
                {currentKv?.name
                  ? `Kontext · ${currentKv.name}`
                  : "Frag mich zur KV-Einstufung & zum Tool"}
              </div>
            </div>
            <button
              onClick={resetChat}
              disabled={sending}
              data-testid="chat-reset"
              title="Neuen Chat beginnen (Verlauf löschen)"
              className="inline-flex items-center justify-center h-8 w-8 rounded-sm hover:bg-white/10 transition-crisp disabled:opacity-50"
              aria-label="Neuer Chat"
            >
              <ArrowCounterClockwise size={14} weight="bold" />
            </button>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close"
              className="inline-flex items-center justify-center h-8 w-8 rounded-sm hover:bg-white/10 transition-crisp"
              aria-label="Schließen"
            >
              <X size={14} weight="bold" />
            </button>
          </div>

          {/* Message list */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-warm-50"
            data-testid="chat-messages"
          >
            {visibleMessages.map((m, idx) => (
              <ChatMessage key={idx} role={m.role} content={m.content} />
            ))}
            {sending && <ChatTyping />}
            {error && (
              <div className="border border-status-abgelehnt-border bg-status-abgelehnt-bg rounded-sm p-3 text-xs text-status-abgelehnt-text">
                {error}
              </div>
            )}
          </div>

          {/* Input row */}
          <div className="border-t border-ink-200 bg-white p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={2}
                maxLength={4000}
                placeholder="Frage stellen… (Enter zum Senden, Shift+Enter für neue Zeile)"
                data-testid="chat-input"
                className="flex-1 resize-none border border-ink-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
              />
              <button
                onClick={send}
                disabled={sending || !input.trim()}
                data-testid="chat-send"
                className="inline-flex items-center justify-center h-9 w-9 rounded-sm bg-brand-400 text-white hover:bg-brand-500 transition-crisp disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Senden"
              >
                <PaperPlaneRight size={14} weight="bold" />
              </button>
            </div>
            <div className="mt-2 flex items-center gap-1.5 text-[10px] text-warm-500">
              <Shield size={10} weight="bold" />
              Verlauf wird nur in Ihrem Browser gespeichert · keine Speicherung am Server
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ChatMessage({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={[
          "max-w-[85%] px-3 py-2 rounded-sm text-sm leading-relaxed whitespace-pre-wrap",
          isUser
            ? "bg-ink-900 text-white"
            : "bg-white border border-ink-200 text-ink-900",
        ].join(" ")}
      >
        {content}
      </div>
    </div>
  );
}

function ChatTyping() {
  return (
    <div className="flex justify-start" data-testid="chat-typing">
      <div className="inline-flex items-center gap-1.5 bg-white border border-ink-200 px-3 py-2 rounded-sm">
        <span className="brand-dot" />
        <span className="brand-dot" />
        <span className="brand-dot" />
      </div>
    </div>
  );
}
